import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  register: (userData) => api.post('/api/auth/register', userData),
  getMe: () => api.get('/api/auth/me'),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/api/auth/reset-password', { token, new_password }),
};

// Deals APIs
export const dealsAPI = {
  getAll: (params) => api.get('/api/deals', { params }),
  getById: (id) => api.get(`/api/deals/${id}`),
  create: (data) => api.post('/api/deals', data),
  update: (id, data) => api.put(`/api/deals/${id}`, data),
  delete: (id) => api.delete(`/api/deals/${id}`),
  updateStage: (id, stage) => api.patch(`/api/deals/${id}/stage`, { stage }),
};

// Clients APIs
export const clientsAPI = {
  getAll: (params) => api.get('/api/clients', { params }),
  getById: (id) => api.get(`/api/clients/${id}`),
  create: (data) => api.post('/api/clients', data),
  update: (id, data) => api.put(`/api/clients/${id}`, data),
  delete: (id, adminPassword) => api.delete(`/api/clients/${id}`, { params: adminPassword ? { admin_password: adminPassword } : {} }),
  convert: (id) => api.post(`/api/clients/${id}/convert`),
};

// Contacts APIs
export const contactsAPI = {
  getAll: (params) => api.get('/api/contacts', { params }),
  getById: (id) => api.get(`/api/contacts/${id}`),
  create: (data) => api.post('/api/contacts', data),
  update: (id, data) => api.put(`/api/contacts/${id}`, data),
  delete: (id) => api.delete(`/api/contacts/${id}`),
};

// Partners APIs
export const partnersAPI = {
  getAll: (params) => api.get('/api/partners', { params }),
  getById: (id) => api.get(`/api/partners/${id}`),
  create: (data) => api.post('/api/partners', data),
  update: (id, data) => api.put(`/api/partners/${id}`, data),
  delete: (id) => api.delete(`/api/partners/${id}`),
};

// Activities APIs
export const activitiesAPI = {
  getAll: (params) => api.get('/api/activities', { params }),
  create: (data) => api.post('/api/activities', data),
  update: (id, data) => api.put(`/api/activities/${id}`, data),
  delete: (id) => api.delete(`/api/activities/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getKPIs: (params) => api.get('/api/dashboard/kpis', { params }),
  getCharts: (params) => api.get('/api/dashboard/charts', { params }),
  getTables: (params) => api.get('/api/dashboard/tables', { params }),
};

// Service Pillars APIs
export const servicePillarsAPI = {
  getAll: () => api.get('/api/service-pillars'),
  create: (data) => api.post('/api/service-pillars', data),
  update: (id, data) => api.put(`/api/service-pillars/${id}`, data),
};

// Pipeline Stages APIs
export const pipelineStagesAPI = {
  getAll: () => api.get('/api/pipeline-stages'),
};

// Users APIs (Admin)
export const usersAPI = {
  getAll: () => api.get('/api/users'),
  getPending: () => api.get('/api/users/pending'),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  approve: (id, status) => api.post(`/api/users/${id}/approve`, { status }),
};

// Audit Logs APIs (Admin)
export const auditLogsAPI = {
  getAll: (params) => api.get('/api/audit-logs', { params }),
};

// Import APIs (Admin)
export const importAPI = {
  analyze: (formData) => api.post('/api/import/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  execute: (importId, applyCleanup) => api.post(`/api/import/execute/${importId}`, { apply_cleanup: applyCleanup }),
};

// Targets APIs (Admin)
export const targetsAPI = {
  getAll: () => api.get('/api/targets'),
  getByYear: (year) => api.get(`/api/targets/${year}`),
  createOrUpdate: (data) => api.post('/api/targets', data),
  delete: (year) => api.delete(`/api/targets/${year}`),
};

// Prospecting APIs
export const prospectingAPI = {
  getStats: () => api.get('/api/prospecting/stats'),
  getCoverageGaps: () => api.get('/api/clients-coverage-gaps'),
};

// AI APIs
export const aiAPI = {
  findContacts: (data) => api.post('/api/ai/find-contacts', data),
  discoverOrganizations: (data) => api.post('/api/ai/discover-organizations', data),
  enrichOrganization: (clientId) => api.post(`/api/ai/enrich-organization/${clientId}`),
  saveContacts: (data) => api.post('/api/ai/save-contacts', data),
  applyEnrichment: (clientId, data) => api.post(`/api/ai/apply-enrichment/${clientId}`, data),
  saveOrganizations: (data) => api.post('/api/ai/save-organizations', data),
  bulkFindContacts: (data) => api.post('/api/ai/bulk-find-contacts', data),
  history: (params) => api.get('/api/ai/history', { params }),
  deleteHistory: (id) => api.delete(`/api/ai/history/${id}`),
  clearHistory: () => api.delete('/api/ai/history'),
};

// Tender Scout APIs
export const tenderAPI = {
  dashboard: () => api.get('/api/tender-scout/dashboard'),
  list: (params) => api.get('/api/tender-scout/tenders', { params }),
  get: (id) => api.get(`/api/tender-scout/tenders/${id}`),
  update: (id, data) => api.patch(`/api/tender-scout/tenders/${id}`, data),
  promote: (id, data) => api.post(`/api/tender-scout/tenders/${id}/promote`, data),
  dismiss: (id, data) => api.post(`/api/tender-scout/tenders/${id}/dismiss`, data),
  markDuplicate: (id, data) => api.post(`/api/tender-scout/tenders/${id}/duplicate`, data),
  sources: () => api.get('/api/tender-scout/sources'),
  createSource: (data) => api.post('/api/tender-scout/sources', data),
  updateSource: (id, data) => api.put(`/api/tender-scout/sources/${id}`, data),
  deleteSource: (id) => api.delete(`/api/tender-scout/sources/${id}`),
  testSource: (id) => api.post(`/api/tender-scout/sources/${id}/test`),
  runSource: (id) => api.post(`/api/tender-scout/sources/${id}/run`),
  runAll: () => api.post('/api/tender-scout/scrape/run-all'),
  scrapeJobs: (params) => api.get('/api/tender-scout/scrape/jobs', { params }),
  scrapeJobStatus: (id) => api.get(`/api/tender-scout/scrape/jobs/${id}`),
  scrapeLog: (params) => api.get('/api/tender-scout/scrape/log', { params }),
  scoreUnscored: () => api.post('/api/tender-scout/score/run'),
  rescoreAll: () => api.post('/api/tender-scout/score/rescore'),
  aiScore: (id) => api.post(`/api/tender-scout/score/ai/${id}`),
  aiDiscoverSources: () => api.post('/api/tender-scout/sources/ai-discover'),
  batchAddSources: (sources) => api.post('/api/tender-scout/sources/batch-add', sources),
  getProfile: () => api.get('/api/tender-scout/profile'),
  updateProfile: (data) => api.put('/api/tender-scout/profile', data),
  getNotifications: () => api.get('/api/tender-scout/notifications'),
  updateNotifications: (data) => api.put('/api/tender-scout/notifications', data),
  runExpiry: () => api.post('/api/tender-scout/expire'),
  schedulerStatus: () => api.get('/api/tender-scout/scheduler/status'),
  schedulerStart: (hour, minute) => api.post(`/api/tender-scout/scheduler/start?hour=${hour || 6}&minute=${minute || 0}`),
  schedulerStop: () => api.post('/api/tender-scout/scheduler/stop'),
  schedulerRunNow: () => api.post('/api/tender-scout/scheduler/run-now'),
  notificationItems: (params) => api.get('/api/tender-scout/notifications/items', { params }),
  notificationUnreadCount: () => api.get('/api/tender-scout/notifications/unread-count'),
  notificationMarkRead: (id) => api.patch(`/api/tender-scout/notifications/items/${id}/read`),
  notificationMarkAllRead: () => api.post('/api/tender-scout/notifications/read-all'),
  notificationDelete: (id) => api.delete(`/api/tender-scout/notifications/items/${id}`),
  notificationConfig: () => api.get('/api/tender-scout/notifications/config'),
  notificationConfigUpdate: (data) => api.put('/api/tender-scout/notifications/config', data),
  exportCSV: (params) => api.get('/api/tender-scout/tenders-export/csv', { params, responseType: 'blob' }),
  exportPDF: (params) => api.get('/api/tender-scout/tenders-export/pdf', { params, responseType: 'blob' }),
};
